import React from "react";

import { dueLine, freshnessDate } from "./freshness";
import { Eyebrow } from "./PlanCard";
import type { PlanFreshness } from "../../Types/AccountPlan";

/** The server's default when the overview predates refresh_days. */
const DEFAULT_REFRESH_DAYS = 30;

/** "every 30 days, set by your admin" */
const intervalNote = (days: number): string =>
  `every ${days} day${days === 1 ? "" : "s"}, set by your admin`;

const meterTone = (ratio: number, overdue: boolean): string =>
  overdue || ratio >= 1
    ? "bg-red-500"
    : ratio >= 0.75
      ? "bg-amber-400"
      : "bg-emerald-500";

interface Props {
  freshness: PlanFreshness | null | undefined;
  /** settings.refresh_days, for the "nothing published" line. */
  refreshDays?: number;
}

/** Step 1 · Plan freshness: how long since publish, against the admin's interval. */
const PlanFreshnessCard: React.FC<Props> = ({ freshness, refreshDays }) => {
  const days = freshness?.refresh_days ?? refreshDays ?? DEFAULT_REFRESH_DAYS;
  return (
    <section
      aria-label="Plan freshness"
      data-testid="plan-freshness"
      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <Eyebrow>Plan freshness</Eyebrow>
      {!freshness ? (
        <p className="text-sm text-gray-500">
          Nothing published yet. Your admin asks for a refresh every {days} day
          {days === 1 ? "" : "s"}.
        </p>
      ) : (
        <>
          {freshness.overdue ? (
            <p className="mb-1 text-sm font-semibold text-red-700">
              {dueLine(freshness)}
            </p>
          ) : null}
          <p className="mb-2 text-sm text-gray-800">
            {freshness.days_since_publish === 0
              ? "Published today"
              : `Published ${freshness.days_since_publish} day${
                  freshness.days_since_publish === 1 ? "" : "s"
                } ago`}
            {freshness.published_plan_version !== null && (
              <> · v{freshness.published_plan_version}</>
            )}
            {freshness.published_at && (
              <>, {freshnessDate(freshness.published_at)}</>
            )}
          </p>
          <div
            role="meter"
            aria-label="Days since publish against the refresh interval"
            aria-valuemin={0}
            aria-valuemax={days}
            aria-valuenow={Math.min(freshness.days_since_publish, days)}
            className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
          >
            <div
              className={`h-full rounded-full ${meterTone(
                freshness.days_since_publish / Math.max(days, 1),
                freshness.overdue
              )}`}
              style={{
                width: `${Math.min(
                  100,
                  (freshness.days_since_publish / Math.max(days, 1)) * 100
                )}%`,
              }}
            />
          </div>
          <p className="text-xs text-gray-500">
            {freshness.overdue ? (
              <>Plans here are refreshed {intervalNote(days)}.</>
            ) : (
              <>
                {dueLine(freshness)} ({intervalNote(days)})
              </>
            )}
          </p>
        </>
      )}
    </section>
  );
};

export default PlanFreshnessCard;
